// A new business's empty Products page offers its kind's usual categories, once
// (docs/plans/agrovet.md, board KindsStarter). Shown while the business has no products
// and no categories; "Not now" hides it on this device.
import { Button, cx, useToast } from '@ims/ui'
import { Check, Plant } from '@ims/ui/icons'
import { useState } from 'react'
import { useLookups } from '../components.tsx'
import { errorMessage, useSettings } from '../hooks.ts'
import { BUSINESS_TYPES, STARTER_CATEGORIES } from '../lib/catalog.ts'
import { insertOp, newCategory } from '../lib/rows.ts'
import { useRepo, useWorkspace } from '../workspace.tsx'

const dismissKey = (tenantId: string) => `starter-categories-dismissed:${tenantId}`

function dismissed(tenantId: string): boolean {
  try {
    return localStorage.getItem(dismissKey(tenantId)) === '1'
  } catch {
    return false
  }
}

/** The offer, or nothing when the business's main kind has none or it has categories. */
export function StarterCategories() {
  const settings = useSettings()
  const lookups = useLookups()
  const repo = useRepo()
  const toast = useToast()
  const { current } = useWorkspace()
  const tenantId = current?.tenant_id ?? ''
  const kind = settings?.business_type ?? 'general'
  const names = STARTER_CATEGORIES[kind]
  const [hidden, setHidden] = useState(() => dismissed(tenantId))
  // What was unticked: the list itself comes with the settings, which may load after this.
  const [skipped, setSkipped] = useState<Set<string>>(() => new Set())
  const [busy, setBusy] = useState(false)
  if (!names || hidden || !tenantId || lookups.categories.length > 0) return null
  const label = BUSINESS_TYPES.find((t) => t.id === kind)?.label.toLowerCase() ?? 'your'
  const chosen = names.filter((n) => !skipped.has(n))

  const add = async () => {
    setBusy(true)
    try {
      await repo.commit(
        chosen.map((name, i) =>
          insertOp('categories', newCategory({ tenantId }, { name, sort_order: i })),
        ),
      )
      toast.success(`Added ${chosen.length} ${chosen.length === 1 ? 'category' : 'categories'}`)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const notNow = () => {
    try {
      localStorage.setItem(dismissKey(tenantId), '1')
    } catch {
      // A private window: it shows again next time, which is fine.
    }
    setHidden(true)
  }

  return (
    <section
      className="grid justify-items-center gap-3 border-b border-border p-6 text-center"
      aria-label="Start with categories"
    >
      <span className="grid size-11 place-items-center rounded-full bg-success-light text-success">
        <Plant className="size-5" />
      </span>
      <div>
        <h2 className="text-base font-bold">Start with {label} categories?</h2>
        <p className="mt-1 text-sm text-muted">
          Untick any you don’t need. You can rename or delete them later.
        </p>
      </div>
      <div className="flex max-w-xl flex-wrap justify-center gap-2">
        {names.map((n) => {
          const on = !skipped.has(n)
          return (
            <label
              key={n}
              className={cx(
                'inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border-[1.5px] px-3 text-sm font-semibold has-focus-visible:ring-2 has-focus-visible:ring-primary/30',
                on ? 'border-primary bg-primary-light' : 'border-border text-muted',
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={on}
                onChange={() =>
                  setSkipped((p) => {
                    const next = new Set(p)
                    if (on) next.add(n)
                    else next.delete(n)
                    return next
                  })
                }
              />
              <span
                aria-hidden
                className={cx(
                  'grid size-4 place-items-center rounded',
                  on ? 'bg-primary text-on-fill' : 'border-[1.5px] border-subtle',
                )}
              >
                {on && <Check className="size-3" />}
              </span>
              {n}
            </label>
          )
        })}
      </div>
      <div className="mt-1 flex flex-wrap justify-center gap-2">
        <Button
          variant="soft"
          loading={busy}
          disabled={chosen.length === 0}
          onClick={() => void add()}
        >
          Add {chosen.length === names.length ? `these ${chosen.length}` : chosen.length}{' '}
          {chosen.length === 1 ? 'category' : 'categories'}
        </Button>
        <Button variant="secondary" disabled={busy} onClick={notNow}>
          Not now
        </Button>
      </div>
    </section>
  )
}
